"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Pencil, Plus, Sparkles } from "lucide-react";

import { DeskCover, DeskEmpty, DeskMore, Seal } from "@/features/home-v2/desk/Desk";
import { Loading } from "@/features/home-v2/ui";
import { useScrollReveal } from "@/features/home-v2/useScrollReveal";
import { dropService } from "@/services/drop.service";
import { useAuthStore } from "@/store/useAuthStore";
import { normalizeDrop } from "../../normalize";
import { canEditDrop, isDropEnded, isDropFinalized, type DropRawStatus } from "../../permissions";
import type { Drop } from "../../types";
import { dropFigures } from "../../dropFigures";
import DropTile from "../DropTile";
import "./my-drops.css";
import { useGridColumns } from "@/hooks/useGridColumns";

/* eslint-disable @typescript-eslint/no-explicit-any -- the drops payload is untyped, as it was in MyDropsClient */

/** Where the drop stands in review — the owner's word for each raw status. */
const REVIEW: Record<DropRawStatus, { fa: string; en: string; tone: "muted" | "accent" | "live" | "gold" | "down" }> = {
  draft: { fa: "پیش‌نویس", en: "Draft", tone: "muted" },
  send_to_review: { fa: "در انتظار بررسی", en: "In review", tone: "accent" },
  accept: { fa: "تأییدشده", en: "Approved", tone: "live" },
  active: { fa: "منتشرشده", en: "Live", tone: "live" },
  reject: { fa: "ردشده", en: "Rejected", tone: "down" },
  expired: { fa: "پایان‌یافته", en: "Expired", tone: "gold" },
  other: { fa: "نامشخص", en: "Unknown", tone: "muted" },
};

/**
 * «دراپ‌های من» — the owner's drops inside the profile's owner tab, in v2.
 * MyDropsClient's fetch, unchanged: every drop of this profile's user,
 * whatever its status, with the bearer token so the unpublished ones come
 * back too (`limit: 100`); a failure reads as an empty shelf. Each drop is
 * the catalogue's own tile over an owner's plate: where it stands in review,
 * and the edit page when the drop's own rule allows it (editable status and
 * not yet finalized on chain — the single drop's gate).
 */
export default function MyDrops({ lang, userId }: { lang: "fa" | "en"; userId: number }) {
  const isFa = lang === "fa";
  const t = (fa: string, en: string) => (isFa ? fa : en);
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const token = useAuthStore((s) => s.token);
  const [drops, setDrops] = useState<Drop[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        setIsLoading(true);
        const res = await dropService.getDrops({ user_id: userId, limit: 100, token }, lang);
        const raw = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.drops) ? res.data.drops : [];
        setDrops(raw.map((r: any) => normalizeDrop(r, lang)));
      } catch {
        setDrops([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [userId, lang, token]);

  const grid = useGridColumns();
  const reveal = useScrollReveal({ columns: grid.columns, loaded: drops.length });
  const inReview = drops.filter((d) => d.rawStatus === "draft" || d.rawStatus === "send_to_review").length;
  // "Minting" only while something is left — a sold-out drop reads sold out everywhere (dropFigures).
  const live = drops.filter((d) => d.status === "minting" && !dropFigures(d).soldOut && !dropFigures(d).empty).length;
  const create = { href: `/${lang}/drops/create`, label: t("ساخت دراپ تازه", "New drop") };

  return (
    <div className="dk dk--tab md">
      <DeskCover
        title={t("دراپ‌های من", "My drops")}
        lead={t("همه‌ی دراپ‌هایتان، از پیش‌نویس تا منتشرشده، با جایگاه هرکدام در بررسی.", "All your drops, from draft to live, with where each stands in review.")}
        create={create}
        tally={isLoading ? [] : [
          { key: "all", label: t("دراپ", "Drops"), value: nf.format(drops.length), tone: "accent" },
          { key: "review", label: t("پیش‌نویس یا در بررسی", "Draft or in review"), value: nf.format(inReview), tone: "muted" },
          { key: "live", label: t("در حال مینت", "Minting now"), value: nf.format(live), tone: "live" },
        ]}
      />

      {isLoading ? <Loading label={t("در حال بارگذاری دراپ‌ها", "Loading your drops")} />
        : drops.length === 0 ? <DeskEmpty icon={<Sparkles />} title={t("هنوز دراپی نساخته‌اید", "You haven't created a drop yet")} body={t("مجموعه‌ای از آثار را برای مینت آماده کنید؛ از پیش‌نویس تا انتشار، جایگاه هر دراپ این‌جا دیده می‌شود.", "Prepare a set of works for people to mint — from draft to live, each drop's progress shows here.")} actions={<><Link className="v2-btn v2-focus" href={create.href}><Plus aria-hidden />{create.label}</Link><Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/drops`}>{t("دیدن دراپ‌ها", "See the drops")}</Link></>} />
        : (
          <>
            <ul ref={grid.ref} className="md-grid">
              {drops.slice(0, reveal.count).map((d) => {
                const review = REVIEW[d.rawStatus] ?? REVIEW.other;
                const editable = canEditDrop(d.rawStatus) && !isDropFinalized(d.confirmationAt) && !isDropEnded(d.endAt) && d.slug;
                return (
                  <li key={d.id} className="md-item">
                    <DropTile drop={d} lang={lang} />
                    <div className="md-plate">
                      <Seal tone={review.tone}>{isFa ? review.fa : review.en}</Seal>
                      {editable ? <Link href={`/${lang}/drops/${d.slug}/edit`} className="v2-btn v2-btn--ghost v2-btn--sm v2-focus"><Pencil aria-hidden />{t("ویرایش", "Edit")}</Link> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            <DeskMore show={reveal.hasMore} busy={false} onMore={reveal.more} sentinel={reveal.sentinel} label={t("دراپ‌های بیشتر", "More drops")} />
          </>
        )}
    </div>
  );
}
